import React, { createContext, useContext, useMemo, useRef, useState, useCallback } from 'react';
import apiService from '../services/api';

const DataCacheContext = createContext(null);

const DEFAULT_TTL_MS = 2 * 60 * 1000; // 2 minutes

export function DataCacheProvider({ children, ttlMs = DEFAULT_TTL_MS }) {
  const cacheRef = useRef({
    students: { data: null, fetchedAt: 0, loading: false },
    courses: { data: null, fetchedAt: 0, loading: false },
  });
  const subscribersRef = useRef({ students: new Set(), courses: new Set() });
  const [, setVersion] = useState(0); // force updates when cache changes

  const notify = (key) => {
    setVersion((v) => v + 1);
    subscribersRef.current[key].forEach((cb) => {
        try { cb(); } catch (_) {}
    });
  };

  const isStale = (key) => {
    const entry = cacheRef.current[key];
    return !entry?.data || (Date.now() - entry.fetchedAt) > ttlMs;
  };

  const getStudents = useCallback(async ({ force = false, background = false } = {}) => {
    const key = 'students';
    const entry = cacheRef.current[key];
    if (!force && !isStale(key)) {
      return entry.data || [];
    }
    if (entry.loading) {
      return entry.data || [];
    }
    const runFetch = async () => {
      try {
        cacheRef.current[key].loading = true;
        const data = await apiService.getStudents();
        cacheRef.current[key] = { data: Array.isArray(data) ? data : [], fetchedAt: Date.now(), loading: false };
        notify(key);
      } catch (e) {
        cacheRef.current[key].loading = false;
        // Keep old data on error
      }
    };
    if (background) {
      runFetch();
      return entry.data || [];
    }
    await runFetch();
    return cacheRef.current[key].data || [];
  }, [ttlMs]);

  const getCourses = useCallback(async ({ force = false, background = false } = {}) => {
    const key = 'courses';
    const entry = cacheRef.current[key];
    if (!force && !isStale(key)) {
      return entry.data || [];
    }
    if (entry.loading) {
      return entry.data || [];
    }
    const runFetch = async () => {
      try {
        cacheRef.current[key].loading = true;
        const data = await apiService.getCourses();
        cacheRef.current[key] = { data: Array.isArray(data) ? data : [], fetchedAt: Date.now(), loading: false };
        notify(key);
      } catch (e) {
        cacheRef.current[key].loading = false;
      }
    };
    if (background) {
      runFetch();
      return entry.data || [];
    }
    await runFetch();
    return cacheRef.current[key].data || [];
  }, [ttlMs]);

  const mutateStudents = useCallback((updater) => {
    const current = cacheRef.current.students.data || [];
    const next = typeof updater === 'function' ? updater(current) : updater;
    cacheRef.current.students = { data: next, fetchedAt: Date.now(), loading: false };
    notify('students');
  }, []);

  const mutateCourses = useCallback((updater) => {
    const current = cacheRef.current.courses.data || [];
    const next = typeof updater === 'function' ? updater(current) : updater;
    cacheRef.current.courses = { data: next, fetchedAt: Date.now(), loading: false };
    notify('courses');
  }, []);

  const value = useMemo(() => ({
    // snapshots
    students: cacheRef.current.students.data || [],
    courses: cacheRef.current.courses.data || [],
    studentsFetchedAt: cacheRef.current.students.fetchedAt,
    coursesFetchedAt: cacheRef.current.courses.fetchedAt,
    studentsLoading: cacheRef.current.students.loading,
    coursesLoading: cacheRef.current.courses.loading,
    // loaders
    getStudents,
    getCourses,
    refreshStudents: () => getStudents({ force: true, background: false }),
    refreshCourses: () => getCourses({ force: true, background: false }),
    prefetchStudents: () => getStudents({ background: true }),
    prefetchCourses: () => getCourses({ background: true }),
    // mutations (to keep cache in sync on create/update/delete)
    mutateStudents,
    mutateCourses,
  }), [getStudents, getCourses, mutateStudents, mutateCourses]);

  return (
    <DataCacheContext.Provider value={value}>{children}</DataCacheContext.Provider>
  );
}

export function useDataCache() {
  const ctx = useContext(DataCacheContext);
  if (!ctx) throw new Error('useDataCache must be used within DataCacheProvider');
  return ctx;
}


